
import { useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import PagarPedidoContainer from '../../../../../../containers/pagarPedido/pagarPedidoContainer';
import { GerarPixResponse } from '../../../../../../types/lotePedido/gerarPixResponse';
import * as S from './adicionarSaldoPixStyles';
import { formatToBRL } from 'base/src/utils/mathUtil';

export type AdicionarSaldoPixProps = {
  campanhaPedidoResgatePagamentoId: number;
  ValorTotal: string;
  loteId?: number;
  carteiraId: number;
  onClose?: () => void;
}

export function AdicionarSaldoPix(props: AdicionarSaldoPixProps) {
  const _pagarPedidoContainer = new PagarPedidoContainer();
  const [pixData, setPixData] = useState<GerarPixResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isWaitingPayment, setIsWaitingPayment] = useState(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const isPixCancelledRef = useRef(false);

  async function handleGerarPix() {
    try {
      setIsGenerating(true);

      const result = await _pagarPedidoContainer.gerarPix(
        props.campanhaPedidoResgatePagamentoId,
        props.carteiraId
      );

      if (result instanceof Error) {
        toast('Erro ao gerar PIX', { type: 'error' });
        return;
      }

      setPixData(result);
      setIsWaitingPayment(true);
      toast('PIX gerado com sucesso!', { type: 'success' });

      // Inicia o SSE para verificar o pagamento
      _pagarPedidoContainer.pixCheckSSE(result.CodigoInterno, (success) => {
        setIsWaitingPayment(false);
        if (success) {
          setIsPaymentConfirmed(true);
          // Aqui você pode adicionar lógica adicional após a confirmação do pagamento
          // Por exemplo, fechar o modal, atualizar dados, etc.
        } else {
          // Só mostra erro se o PIX não foi cancelado pelo usuário
          if (!isPixCancelledRef.current) {
            toast('Erro na confirmação do pagamento', { type: 'error' });
          }
        }
      });

    } catch (error) {
      toast('Erro ao gerar PIX', { type: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleCopyPixCode() {
    if (pixData?.PixCopiaCola) {
      try {
        await navigator.clipboard.writeText(pixData.PixCopiaCola);
        toast('Código PIX copiado!', { type: 'success' });
      } catch (error) {
        toast('Erro ao copiar código PIX', { type: 'error' });
      }
    }
  }

  function handleResetPix() {
    isPixCancelledRef.current = true;
    setPixData(null);
    setIsWaitingPayment(false);
    setIsPaymentConfirmed(false);
    _pagarPedidoContainer.resetPix();
    toast('PIX cancelado', { type: 'info' });

    // Reset o flag após um tempo para permitir novos PIXs
    setTimeout(() => {
      isPixCancelledRef.current = false;
    }, 1000);
  }

  return (
    <S.Container>

      {!isWaitingPayment && (
        <S.AvisoContainer>
          <S.AvisoTitle>
            <Icon icon="ph:warning" />
            Importante
          </S.AvisoTitle>
          <S.AvisoText>
            Após efetuar o pagamento via PIX, aguarde a confirmação automática do sistema.
            O processo pode levar alguns minutos.
          </S.AvisoText>
        </S.AvisoContainer>
      )}

      {isWaitingPayment && (
        <S.AvisoContainer style={{ backgroundColor: '#fff3cd', borderColor: '#ffeaa7' }}>
          <S.AvisoText style={{ color: '#856404' }}>
            Realize o pagamento via PIX e aguarde a confirmação automática.
            O sistema irá notificar quando o pagamento for processado.
          </S.AvisoText>
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <button
              onClick={handleResetPix}
              style={{
                background: '#dc3545',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <Icon icon="ph:x" />
              Cancelar PIX
            </button>
          </div>
        </S.AvisoContainer>
      )}

      {!pixData && (
        <S.InfoSection>
          <S.SectionTitle>
            <Icon icon="ph:info" />
            Informações do Pagamento
          </S.SectionTitle>

          <S.InfoGrid>
            <S.InfoCard>
              <S.InfoLabel>Valor Total</S.InfoLabel>
              <S.InfoValue size="lg" color="success">
                {formatToBRL(props.ValorTotal)}
              </S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.InfoLabel>ID do Pagamento</S.InfoLabel>
              <S.InfoValue>
                {props.campanhaPedidoResgatePagamentoId}
              </S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.InfoLabel>Carteira ID</S.InfoLabel>
              <S.InfoValue>
                {props.carteiraId}
              </S.InfoValue>
            </S.InfoCard>
          </S.InfoGrid>
        </S.InfoSection>
      )}


      {!pixData && (
        <S.ComprovanteContainer>
          <S.ComprovanteButton
            onClick={handleGerarPix}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Icon icon="ph:spinner" className="animate-spin" />
                Gerando PIX...
              </>
            ) : (
              <>
                <Icon icon="ph:qr-code" />
                Gerar PIX
              </>
            )}
          </S.ComprovanteButton>
        </S.ComprovanteContainer>
      )}

      {pixData && !isPaymentConfirmed && (
        <S.InfoSection>
          <S.SectionTitle>
            <Icon icon="ph:check-circle" color="#28a745" />
            PIX Gerado com Sucesso
          </S.SectionTitle>

          <S.InfoGrid>
            <S.InfoCard fullWidth>
              <S.InfoLabel>QR Code</S.InfoLabel>
              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <img
                  src={`data:image/png;base64,${pixData.QrCode}`}
                  alt="QR Code PIX"
                  style={{
                    maxWidth: '200px',
                    height: 'auto',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '8px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
            </S.InfoCard>

            <S.InfoCard fullWidth>
              <S.InfoLabel>Código PIX Copia e Cola</S.InfoLabel>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px'
              }}>
                <S.InfoValue style={{
                  flex: 1,
                  wordBreak: 'break-all',
                  fontSize: '12px',
                  padding: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}>
                  {pixData.PixCopiaCola}
                </S.InfoValue>
                <button
                  onClick={handleCopyPixCode}
                  style={{
                    background: '#007bff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    minWidth: 'fit-content'
                  }}
                >
                  <Icon icon="ph:copy" />
                  Copiar
                </button>
              </div>
            </S.InfoCard>
          </S.InfoGrid>
        </S.InfoSection>
      )}

      {isPaymentConfirmed && pixData && (
        <S.InfoSection>
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <div style={{
              fontSize: '80px',
              color: '#28a745',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Icon icon="ph:check-circle-fill" />
            </div>

            <S.SectionTitle style={{
              color: '#28a745',
              marginBottom: '16px',
              fontSize: '24px',
              textAlign: 'center'
            }}>
              Pagamento Confirmado!
            </S.SectionTitle>

            <S.AvisoText style={{
              color: '#495057',
              fontSize: '16px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              Seu pagamento PIX foi processado com sucesso.
              <br />
              Valor: <strong>R$ {formatToBRL(pixData.PixValue)}</strong>
            </S.AvisoText>

            <S.InfoGrid style={{ marginTop: '24px' }}>
              <S.InfoCard>
                <S.InfoLabel>Depósito ID</S.InfoLabel>
                <S.InfoValue color="success">
                  {pixData.DepositoID}
                </S.InfoValue>
              </S.InfoCard>

              <S.InfoCard>
                <S.InfoLabel>Código Interno</S.InfoLabel>
                <S.InfoValue color="success">
                  {pixData.CodigoInterno}
                </S.InfoValue>
              </S.InfoCard>
            </S.InfoGrid>

            <div style={{ marginTop: '24px' }}>
              <button
                onClick={handleResetPix}
                style={{
                  background: '#6c757d',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                <Icon icon="ph:arrow-left" />
                Fazer Novo PIX
              </button>
            </div>
          </div>
        </S.InfoSection>
      )}
    </S.Container>
  );
}

export default AdicionarSaldoPix;